import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { useAppWeb3 } from '../../hooks'
import type { ChainId } from '../../sdk'
import store, { type AppDispatch } from '../../state'
import { addTransaction, finalizeTransaction } from '../../state/transactions/actions'
import { fetchSerializableReceipt } from '../../state/transactions/receiptRpc'
import { isDismissedMobileSwapHash, patchMobileSwapSession, readMobileSwapSession } from './mobileSwapSession'
import { recoverMobileSwapSessionHash } from './recoverMobileSwapSessionHash'

type UseMobileSwapReceiptSyncOptions = {
  txHash?: string
  onConfirmed: (hash: string) => void
  onSessionUpdated?: () => void
}

/** Poll receipt on-chain for a mobile swap hash (MetaMask reload / background tab). */
export function useMobileSwapReceiptSync({
  txHash,
  onConfirmed,
  onSessionUpdated,
}: UseMobileSwapReceiptSyncOptions): void {
  const { account, chainId, library } = useAppWeb3()
  const dispatch = useDispatch<AppDispatch>()
  const onConfirmedRef = useRef(onConfirmed)
  const onSessionUpdatedRef = useRef(onSessionUpdated)
  onConfirmedRef.current = onConfirmed
  onSessionUpdatedRef.current = onSessionUpdated

  useEffect(() => {
    if (!account || !chainId || !library) return

    let cancelled = false

    const completeIfConfirmed = (hash: string) => {
      if (isDismissedMobileSwapHash(hash)) return
      patchMobileSwapSession({ phase: 'confirmed', hash })
      onConfirmedRef.current(hash)
    }

    const ensureRegistered = (hash: string, summary?: string) => {
      const txs = store.getState().transactions[chainId as ChainId] ?? {}
      if (txs[hash]) return
      dispatch(
        addTransaction({
          chainId: chainId as ChainId,
          hash,
          from: account,
          summary,
        }),
      )
    }

    const poll = async () => {
      if (cancelled) return

      let session = readMobileSwapSession()
      if (session && session.account.toLowerCase() === account.toLowerCase() && session.chainId === chainId) {
        if (!session.hash && (session.phase === 'wallet' || session.phase === 'submitted')) {
          session = (await recoverMobileSwapSessionHash(library, account, chainId)) ?? session
          if (session.hash) {
            onSessionUpdatedRef.current?.()
          }
        }
      }

      const hash = txHash ?? session?.hash
      if (!hash || isDismissedMobileSwapHash(hash)) return
      if (session && session.account.toLowerCase() !== account.toLowerCase()) return
      if (session && session.chainId !== chainId) return

      ensureRegistered(hash, session?.summary)

      const cached = store.getState().transactions[chainId as ChainId]?.[hash]?.receipt
      if (cached) {
        if (cached.status === 0) return
        completeIfConfirmed(hash)
        return
      }

      const receipt = await fetchSerializableReceipt(library, hash)
      if (cancelled || !receipt) return

      dispatch(finalizeTransaction({ chainId: chainId as ChainId, hash, receipt }))
      if (receipt.status === 0) return
      completeIfConfirmed(hash)
    }

    void poll()
    const intervalId = window.setInterval(() => void poll(), 1000)

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void poll()
      window.setTimeout(() => void poll(), 400)
    }

    window.addEventListener('pageshow', onVisible)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      window.removeEventListener('pageshow', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [account, chainId, dispatch, library, txHash])
}
